import {
    Component,
    EventEmitter,
    Injector,
    Input,
    Output,
    inject,
    signal
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { subscribeInComponent } from "src/app/shared/angular-utils";

import {
    $state,
    restManager,
    sessionManager,
} from 'app/ajs-upgraded-providers';

import { Subject, filter, merge, takeUntil } from 'rxjs';

import {
    ConfigurationManagerService,
    ConfigurationManagerStoreService,
    ConfigurationPermissions,
    ConnectionService,
    EntityManagerService,
    StorageManagerService,
    SystemUtilsService,
    ThemeModeService,
    UserActiveSessionManagerService
} from 'src/app/core';

import {
    AutomaticPrintSettingsDialogService,
    OpenDialogsService,
    PendingPrintsDialogService,
    QrcodeWaiterShortcutDialogService
} from 'src/app/dialogs';
import { ChangeSaleNameDialogService } from 'src/app/dialogs/change-sale-name-dialog';

import {
    ActiveSaleService,
    SaleBatchPrintService
} from 'src/app/features';
import { AlvoloStatus } from 'src/app/features/settings/settings-alvolo/model/alvolo-status';
import { SettingsAlvoloStateService } from 'src/app/features/settings/settings-alvolo/service/settings-alvolo.state.service';


@Component({
	standalone: false,
    selector: 'app-fixed-nav-list',
    templateUrl: './fixed-nav-list.component.html',
    styleUrls: ['./fixed-nav-list.component.scss'],
})
export class FixedNavListComponent {
    //Injections
    private readonly automaticPrintSettingsDialogService = inject(AutomaticPrintSettingsDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly connectionService = inject(ConnectionService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly pendingPrintsDialogService = inject(PendingPrintsDialogService);
    private readonly qrcodeWaiterShortcutDialogService = inject(QrcodeWaiterShortcutDialogService);
    private readonly saleBatchPrintService = inject(SaleBatchPrintService);
    private readonly sessionManager = inject(sessionManager);
    private readonly storageManagerService = inject(StorageManagerService);
    private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
    private readonly settingsAlvoloStateService = inject(SettingsAlvoloStateService);
    protected readonly state = inject($state);
    protected readonly themeModeService = inject(ThemeModeService);
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly injector = inject(Injector);
    private readonly restManager = inject(restManager);
    private readonly translateService = inject(TranslateService);
    private readonly systemUtilsService = inject(SystemUtilsService);

    //Inputs
    @Input() currentRoute = '';

    //Outputs
    @Output() goTo = new EventEmitter<string>();
    @Output() menuClickOpenSettings = new EventEmitter<void>();
    @Output() automaticPrintOpenSettings = new EventEmitter<void>();

    private user = this.userActiveSessionManagerService.getSession()!;
    protected avatar = signal(`${this.user.first_name?.charAt(0).toUpperCase() || ''}${this.user.last_name?.charAt(0).toUpperCase() || ''}`);
    protected operatorName = this.user?.first_name && this.user?.last_name ? `${this.user.first_name} ${this.user.last_name}` : this.user.username;
    protected enableUserLogoutPin = this.configurationManagerService.getPreference('users.enable_user_logout_pin') || false;

    protected pendingPrints: number = 0;
    protected externalOrdersEnabled = this.saleBatchPrintService.isExternalOrdersPrintEnabled();

    private destroy$ = new Subject<void>();
    protected externalSalesEnabled = this.saleBatchPrintService.isExternalSalesPrintEnabled();
    protected firstDot: boolean = false;
    protected firstDotAlvolo: boolean = false;
    private modulesPermissions = this.configurationManagerService.getModulesStatus();
    protected enabledModules = this.modulesPermissions.enabledModules;
    protected secondDot: boolean = false;
    protected secondDotAlvolo: boolean = false;
    protected shopStatus: AlvoloStatus = {
        crc: '',
        payload: {
            sales_enabled: false,
            bookings_enabled: false
        }
    };

    protected pienissimoUrl?: string;

    public showShopStatusAlvolo = false;
    public waitFourSecondApiRateLimit = false;

    protected isTablesModuleEnabled: boolean = false;
    protected showButtons: boolean = false;
    protected shopName: string = '';
    protected showAutomaticPrinter = false;
    protected canExitUser = false;
    protected canSwitchUser = false;

    public isConnectionOffline = this.connectionService.isOffline;
    protected settingsEnabled = this.configurationManagerService.isFunctionUserEnabledOptin('settings.enabled');
    protected deselectSalesFromMenuBar: boolean = this.configurationManagerService.getPreference('cashregister.deselect_sales_from_menu_bar') || false;
    protected tablesManageTables = false;

    private async updatePendingPrints() {
        this.pendingPrints = (await this.storageManagerService.getCollectionSize('pending_prints'));
    }

    private isInitializePermissions() {
        this.tablesManageTables = this.configurationManagerService.isUserPermitted('tables.manage_tables');
    }

    private initializeConfig() {
        this.isTablesModuleEnabled = this.configurationManagerService.isModuleEnabled('tables');
        this.showButtons = !!this.configurationManagerService.getPreference('migration.fix_navbar_buttons');
        this.shopName = this.configurationManagerService.getPreference('general.shopname') || this.user.shop!.name!;
        this.showAutomaticPrinter = !!this.configurationManagerService.getPreference('general.auto_print.enable');
        this.canExitUser = this.configurationManagerService.getPreference('users.enable_user_logout') !== false;
        this.canSwitchUser = this.configurationManagerService.getPreference('users.enable_user_switch') !== false;
        this.pienissimoUrl = this.configurationManagerService.getPreference('pienissimo.url') ?? undefined;
    }

    private updateExternalPrintStatus() {
        this.externalOrdersEnabled = this.saleBatchPrintService.isExternalOrdersPrintEnabled();
        this.externalSalesEnabled = this.saleBatchPrintService.isExternalSalesPrintEnabled();

        if (this.externalOrdersEnabled && this.externalSalesEnabled) {
            this.firstDot = true;
            this.secondDot = true;
        } else if (!this.externalOrdersEnabled && !this.externalSalesEnabled) {
            this.firstDot = false;
            this.secondDot = false;
        } else if (this.externalOrdersEnabled || this.externalSalesEnabled) {
            this.firstDot = true;
            this.secondDot = false;
        }
    }

    private updateAlvoloStatus() {
        if (this.shopStatus.payload.sales_enabled && this.shopStatus.payload.bookings_enabled) {
            this.firstDotAlvolo = true;
            this.secondDotAlvolo = true;
        } else if (!this.shopStatus.payload.sales_enabled && !this.shopStatus.payload.bookings_enabled) {
            this.firstDotAlvolo = false;
            this.secondDotAlvolo = false;
        } else if (this.shopStatus.payload.sales_enabled || this.shopStatus.payload.bookings_enabled) {
            this.firstDotAlvolo = true;
            this.secondDotAlvolo = false;
        }

        setTimeout(() => {
            this.waitFourSecondApiRateLimit = false;
        }, 4000);
    }

    constructor() {
        this.initializeConfig();
        this.isInitializePermissions();


        subscribeInComponent(ConfigurationManagerStoreService.shopPreferencesUpdate$, () => {
            this.initializeConfig();
        });

        subscribeInComponent(ConfigurationManagerStoreService.userSettingsUpdate$, () => {
            this.isInitializePermissions();
        })

        // The flags of the automatic print can also be changed from the settings page
        subscribeInComponent(
            merge(toObservable(this.saleBatchPrintService.externalOrdersPrint), toObservable(this.saleBatchPrintService.externalSalesPrint)),
            () => this.updateExternalPrintStatus()
        );

        //Init Alvòlo status
        const modulesPermissions = this.configurationManagerService.getModulesStatus();
        const alvolo_menu = modulesPermissions.enabledModules['alvolo_menu'] || false;

        if(alvolo_menu) {
            this.showShopStatusAlvolo = true;
            this.settingsAlvoloStateService.shopStatus$.subscribe((shopStatus) => {
                this.shopStatus = shopStatus;
                this.updateAlvoloStatus();
            });
        } else {
            this.showShopStatusAlvolo = false;
        }

        //Init external print status
        this.updateExternalPrintStatus();

        //Init print errors store
        this.updatePendingPrints();
    }

    ngOnInit() {
        this.settingsAlvoloStateService.shopStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((shopStatus) => {
            this.shopStatus = shopStatus;
        });

        StorageManagerService.storageUpdates$.pipe(
            takeUntil(this.destroy$),
            filter((upd) => upd.entityName === 'pending_prints'),
        ).subscribe((upd) => this.updatePendingPrints());
    }

    showNewFeature(feature: string): boolean {
        return this.configurationManagerService.isModuleAngular(feature);
    }

    isUserPermitted(permissionName: keyof ConfigurationPermissions) {
        return this.configurationManagerService.isUserPermitted(permissionName);
    }

    logoutCloseSessionsDeep() {
        this.sessionManager.logoutCloseSessionsDeep(true);
    }

    logoutActiveUserSession() {
        this.sessionManager.logoutActiveUserSession(true);
    }

    async switchUser() {
        if(this.deselectSalesFromMenuBar){
            const currentSale = this.activeSaleService.currentSale;

            if(currentSale.status === "open"){
                const res = await this.injector.get(ChangeSaleNameDialogService).openDialog({saleName: this.activeSaleService.currentSale.name});

                if (!res?.saleName) {
                    return;
                }
                await this.activeSaleService.saveSale(res.saleName);
                await this.activeSaleService.loadSale(undefined);
            }
        }

        this.state.go('app.lockScreen');
    }

    async goToState(state: string) {
        if(this.deselectSalesFromMenuBar && this.currentRoute === 'app.new.cashregister.content.showcase') {
            const currentSale = this.activeSaleService.currentSale;

            if((currentSale.status === "open" && currentSale.table_id) || currentSale.order_type === "delivery" || currentSale.order_type === "take_away"){
                await this.deselectSaleAction();
            }

            if(currentSale.status === "open" && !currentSale.table_id && currentSale.order_type !== "delivery" && currentSale.order_type !== "take_away"){
                const res = await this.injector.get(ChangeSaleNameDialogService).openDialog({saleName: this.activeSaleService.currentSale.name});

                if (!res?.saleName) {
                    return;
                }
                await this.activeSaleService.saveSale(res.saleName);
                await this.activeSaleService.loadSale(undefined);
            }
        }

        this.state.go(state);
    }

    protected goToItems() {
        this.goTo.emit(
            this.configurationManagerService.isModuleAngular('items')
                ? 'new.items.showcase'
                : 'items.showcase'
        );
    }

    async deselectSaleAction() {
        await this.activeSaleService.loadSale(undefined);
    }

    menuClick($event: MouseEvent) {
        // $event.stopPropagation();
        this.menuClickOpenSettings.emit();
    }

    openOfflineDialog() {
        return this.openDialogsService.openOfflineDialog();
    }

    openPrintErrorsDialog() {
        this.pendingPrintsDialogService.openDialog();
    }

    openQrcodeWaiterShortcutDialog() {
        this.qrcodeWaiterShortcutDialogService.openDialog();
    }

    automaticPrinterMenu() {
        this.automaticPrintOpenSettings.emit();
    }

    async openAutomaticPrintSettingsDialog() {
        const documentPrinters = await this.entityManagerService.printers.fetchCollectionOffline({ type_in: ['fiscal', 'rt', 'receipt'] });
        const nonFiscalDocuments = await this.entityManagerService.nonfiscalDocuments.fetchCollectionOffline();

        return this.automaticPrintSettingsDialogService.openDialog({data: {documentPrinters, nonFiscalDocuments}});
    }

    openPienissimo() {
        if(!this.pienissimoUrl) {
            return
        }

        this.systemUtilsService.openUrlInNewWindow(this.pienissimoUrl, { maximize: true });
    }

    private handlePienissimoError(error: any) {
        const errResponse = error.data;

        this.openDialogsService.openSnackBarTilby(
            errResponse?.error?.data?.message || errResponse?.message,
            "TOAST.SAVED.ACTION",
            { duration: 3000 },
        );
    }

    syncProductsPienissimo() {
        this.restManager.getOne("pienissimo/sync")
        .then(() => {
            this.openDialogsService.openSnackBarTilby(this.translateService.instant('SETTINGS.SETTINGS.PIENISSIMO_SYNC_PRODUCTS_ACTIVATED'), 'TOAST.SAVED.ACTION',{duration:3000});
        })
        .catch((err: any) => this.handlePienissimoError(err));
    }

    syncTablesPienissimo() {
        this.restManager.getOne("pienissimo/sync_tables")
        .then(() => {
            this.openDialogsService.openSnackBarTilby(this.translateService.instant('SETTINGS.SETTINGS.PIENISSIMO_SYNC_TABLES_ACTIVATED'), 'TOAST.SAVED.ACTION',{duration:3000});
        })
        .catch((err: any) => this.handlePienissimoError(err));
    }

    editAlvoloSale() {
        const payload = {
            "payload": {
                "sales_enabled": !this.shopStatus.payload.sales_enabled
            },
            "crc": this.shopStatus.crc
        }

        this.settingsAlvoloStateService.editAlvoloSale(payload);
        this.waitFourSecondApiRateLimit = true;
    }

    editAlvoloBookings() {
        const payload = {
            "payload": {
                "bookings_enabled": !this.shopStatus.payload.bookings_enabled
            },
            "crc": this.shopStatus.crc
        }

        this.settingsAlvoloStateService.editAlvoloBookings(payload);
        this.waitFourSecondApiRateLimit = true;
    }

    async onExternalPrintSettingChange(settingName: 'orders' | 'sales') {
        switch (settingName) {
            case 'sales':
                const defaultPrinterId = this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_printer_id')
                const defaultDocumentId = this.configurationManagerService.getShopPreference('cashregister.batch_sale_print.default_document_id')

                if (!defaultPrinterId || !defaultDocumentId) {
                    const res = await this.openAutomaticPrintSettingsDialog();

                    if (!res) {
                        return;
                    }
                }

                this.saleBatchPrintService.setExternalSalesPrint(!this.saleBatchPrintService.isExternalSalesPrintEnabled());
                break;
            case 'orders':
                this.saleBatchPrintService.setExternalOrdersPrint(!this.saleBatchPrintService.isExternalOrdersPrintEnabled());
                break;
        }

        this.updateExternalPrintStatus();
    };

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
